import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import FormInput, { SSNInput } from "../FormInput";
import { Formik, Field, Form } from "formik";

import { Button, Alert, Typography, Grid } from "@mui/material";

import AuthService from "../../services/auth.service";
import { useAuth } from "../../hooks/useAuth";
import linkifyEmails from "../../lib/linkifyEmails";

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string().required("Email is required").email("Email is invalid"),
  npn: Yup.string().required("NPN is required"),
  ssn: Yup.string()
    .required("Social Security Number is required")
    .matches(/^\d{3}-?\d{2}-?\d{4}$/, "Must be a SSN of the form XXX-YY-ZZZZ"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .max(40, "Password must not exceed 40 characters"),
  passwordConfirmation: Yup.string()
    .required("Confirm Password is required")
    .oneOf([Yup.ref("password")], "Passwords must match"),
});

const messageStyle = { marginBottom: 2 };

const Signup = () => {
  const navigate = useNavigate();
  const { refetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // #110 - a fresh account always continues into the contract flow, where the
  // wizard machinery decides what follows (a new agent gets the mandatory
  // paperwork; a matched agent gets "Is this you?" and then the dashboard).
  // The incoming ?redirect= (stamped by RequireAuth, e.g. "/" when a visitor
  // arrives at the site root) is deliberately NOT honored after signup: it
  // stranded brand-new agents on the dashboard before their mandatory
  // DocuSign. It is only passed through to the sign-in link, where a RETURNING
  // user does want to land back where they were headed.
  const incomingRedirect = new URLSearchParams(window.location.search).get(
    "redirect"
  );

  const handleSignup = (data) => {
    setMessage("");
    setLoading(true);

    async function doSignup() {
      try {
        await AuthService.register({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          npn: data.npn,
          ssn: data.ssn,
          password: data.password,
        });
        await refetch();
        navigate("/contract");
      } catch (error) {
        const resMessage =
          error.response?.data?.message || error.message || error.toString();
        setLoading(false);
        setMessage(resMessage);
      }
    }
    doSignup();
  };

  const loginHref = `/login?redirect=${encodeURIComponent(
    incomingRedirect || "/contract"
  )}`;

  return (
    <div className="signup-form">
      <Typography variant="h5" sx={{ marginBottom: 2 }}>
        Create your account
      </Typography>
      {message && (
        <Alert sx={messageStyle} severity="error">
          {linkifyEmails(message)}
        </Alert>
      )}
      <Formik
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          npn: "",
          ssn: "",
          password: "",
          passwordConfirmation: "",
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => handleSignup(values)}
      >
        {() => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Field
                  name="firstName"
                  label="First Name"
                  fullWidth
                  component={FormInput}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6}>
                <Field
                  name="lastName"
                  label="Last Name"
                  fullWidth
                  component={FormInput}
                  variant="outlined"
                />
              </Grid>
            </Grid>
            <Field
              name="email"
              label="E-Mail"
              fullWidth
              component={FormInput}
              variant="outlined"
            />
            <Field
              name="npn"
              label="NPN"
              fullWidth
              component={FormInput}
              variant="outlined"
            />
            <Field
              name="ssn"
              label="Social Security Number"
              fullWidth
              component={SSNInput}
              variant="outlined"
            />
            <Field
              name="password"
              label="Password"
              type="password"
              fullWidth
              component={FormInput}
              variant="outlined"
            />
            <Field
              name="passwordConfirmation"
              label="Confirm Password"
              type="password"
              fullWidth
              component={FormInput}
              variant="outlined"
            />
            <Button
              type="submit"
              disabled={loading}
              fullWidth
              variant="contained"
              sx={{ marginTop: 1 }}
            >
              Create Account
            </Button>
          </Form>
        )}
      </Formik>
      <Button
        onClick={() => navigate(loginHref)}
        fullWidth
        variant="outline"
        sx={{ marginTop: 1 }}
      >
        Already have an account? Sign in
      </Button>
    </div>
  );
};

export default Signup;
